import React, { useState, useRef, useEffect } from "react";

/** Available dropdown sizes. */
export type DropDownSize = "sm" | "md" | "lg";

/**
 * - `sm` => h-8 (32px)
 * - `md` => ~40px (if no custom height is provided)
 * - `lg` => 48px
 */
const sizeClasses: Record<DropDownSize, string> = {
  sm: "h-8 px-2 gap-1 text-sm",
  md: "py-1.5 px-3 gap-2 text-sm",
  lg: "h-12 px-4 gap-2 text-base",
};

/** Pequeño ícono de flecha abajo. */
function ChevronDownIcon({ className = "" }: { className?: string }) {
  return (
    <svg className={`h-4 w-4 ${className}`} viewBox="0 0 20 20" fill="currentColor">
      <path
        fillRule="evenodd"
        d="M10 12a.997.997 0 01-.707-.293l-4-4
           a1 1 0 111.414-1.414L10 9.586
           l3.293-3.293a1 1 0 011.414
           1.414l-4 4A.997.997 0 0110
           12z"
        clipRule="evenodd"
      />
    </svg>
  );
}

interface GenericSearchableDropdownProps {
  /** Array of items to display in the dropdown */
  items: string[];
  /** Current selected value */
  value: string;
  /** Callback when selection changes */
  onSelect: (value: string) => void;
  /** Placeholder text when no value is selected */
  defaultLabel?: string;
  /** Dropdown size */
  size?: DropDownSize;
  /** CSS class for the dropdown container */
  className?: string;
  /** CSS class for width */
  widthClass?: string;
  /** CSS class for minimum width */
  minWidthClass?: string;
  /** CSS class for maximum width */
  maxWidthClass?: string;
  /** Whether the dropdown is disabled */
  disabled?: boolean;
  /** Message to show when no items are available */
  emptyItemsMessage?: string;
  /** Whether to allow custom values (not in the items list) */
  allowCustomValues?: boolean;
  /** Callback when a custom value is entered */
  onCustomValue?: (value: string) => void;
}

const GenericSearchableDropdown: React.FC<GenericSearchableDropdownProps> = ({
  items = [],
  value,
  onSelect,
  defaultLabel = "Select a value",
  size = "md",
  className = "",
  widthClass = "w-full",
  minWidthClass = "min-w-[140px]",
  maxWidthClass = "max-w-[200px]",
  disabled = false,
  emptyItemsMessage = "No options available",
  allowCustomValues = true,
  onCustomValue,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [inputValue, setInputValue] = useState(value || "");
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Filter items based on search term
  const filteredItems = items.filter(item =>
    item.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Update input value when value prop changes
  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm("");
        // Reset input value to the selected value if no custom value was entered
        if (!allowCustomValues || !inputValue || items.includes(inputValue)) {
          setInputValue(value || "");
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [value, inputValue, items, allowCustomValues]);

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setSearchTerm(newValue);
    
    // If dropdown is not open, open it when user starts typing
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  // Handle input focus
  const handleInputFocus = () => {
    setIsOpen(true);
    // Select all text when focusing
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  // Handle item selection
  const handleItemSelect = (item: string) => {
    setInputValue(item);
    setSearchTerm("");
    setIsOpen(false);
    onSelect(item);
  };

  // Handle input key events
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (isOpen && filteredItems.length > 0) {
        // Select first filtered item
        handleItemSelect(filteredItems[0]);
      } else if (allowCustomValues && inputValue.trim()) {
        // Handle custom value
        const customValue = inputValue.trim();
        setInputValue(customValue);
        setIsOpen(false);
        setSearchTerm("");
        onSelect(customValue);
        if (onCustomValue) {
          onCustomValue(customValue);
        }
      }
    } else if (e.key === "Escape") {
      setIsOpen(false);
      setSearchTerm("");
      setInputValue(value || "");
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  // Handle dropdown toggle
  const handleDropdownToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (!isOpen) {
      // Focus input when opening dropdown
      setTimeout(() => {
        inputRef.current?.focus();
      }, 0);
    }
  };

  // Get size classes for the input
  const inputSizeClass = sizeClasses[size];
  const buttonWidthClass = `${widthClass} ${minWidthClass} ${maxWidthClass}`;

  return (
    <div
      ref={dropdownRef}
      className={`relative ${className}`}
    >
      {/* Input field */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={value ? "" : defaultLabel}
          disabled={disabled}
          className={`
            ${buttonWidthClass} ${inputSizeClass}
            text-semantic-text-secondary bg-semantic-bg-primary-alt
            border border-semantic-border-secondary rounded-sm
            disabled:bg-semantic-bg-disabled
            disabled:text-semantic-text-disabled
            disabled:cursor-not-allowed
            hover:bg-semantic-state-active transition-colors
            focus:outline-none
            pr-8
          `}
        />
        
        {/* Dropdown arrow */}
        <button
          type="button"
          onClick={handleDropdownToggle}
          disabled={disabled}
          className={`
            absolute right-2 top-1/2 transform -translate-y-1/2
            p-1 rounded
            text-semantic-text-secondary
            hover:text-semantic-text-primary
            focus:outline-none
            disabled:cursor-not-allowed
            transition-transform duration-200
            ${isOpen ? 'rotate-180' : ''}
          `}
        >
          <ChevronDownIcon className="ml-2" />
        </button>
      </div>

      {/* Dropdown menu */}
      {isOpen && (
        <div className={`
          absolute mt-1
          ${widthClass} ${minWidthClass} ${maxWidthClass}
          bg-semantic-bg-primary-alt
          border border-semantic-border-secondary
          rounded-sm
          z-50
        `}>
          <ul className="py-1 max-h-60 overflow-y-auto">
            {filteredItems.length > 0 ? (
              filteredItems.map((item, index) => (
                <li key={index}>
                  <button
                    type="button"
                    onClick={() => handleItemSelect(item)}
                    className="
                      w-full text-left
                      px-3 py-2
                      text-sm
                      text-semantic-text-secondary
                      hover:bg-semantic-state-active
                      hover:text-semantic-text-brand
                      transition-colors
                    "
                  >
                    {item}
                  </button>
                </li>
              ))
            ) : (
              <li>
                <div className="flex items-center justify-center px-3 py-2 text-sm text-semantic-text-secondary cursor-default">
                  <span>{searchTerm ? "No matching items" : emptyItemsMessage}</span>
                </div>
              </li>
            )}
            
            {/* Show option to add custom value if no exact match and custom values are allowed */}
            {allowCustomValues && searchTerm && !items.includes(searchTerm) && (
              <li>
                <button
                  type="button"
                  onClick={() => {
                    const customValue = searchTerm.trim();
                    setInputValue(customValue);
                    setIsOpen(false);
                    setSearchTerm("");
                    onSelect(customValue);
                    if (onCustomValue) {
                      onCustomValue(customValue);
                    }
                  }}
                  className="
                    w-full text-left
                    px-3 py-2
                    text-sm
                    text-semantic-text-brand
                    hover:bg-semantic-state-active
                    hover:text-semantic-text-brand
                    transition-colors
                    border-t border-semantic-border-secondary
                  "
                >
                  Add &quot;{searchTerm}&quot;
                </button>
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
};

export default GenericSearchableDropdown;
